import React, { useEffect, useState } from "react";
// Standard storage functions
import { getItem, getStorage } from "../utils/storage";
// Checklist-specific storage functions
import {
    getAllSavedChecklists,
    deleteChecklistData,
    migrateToIndexedDB,
    cleanupIndexedDB
} from "../utils/storage";
import { useNavigate } from "react-router-dom";
import { Page, Button, AlertDialog } from "react-onsenui";
import { Icon } from "@iconify/react";
import {
    Box,
    CircularProgress,
    Chip,
    ListItemButton,
    Paper,
    Typography,
} from "@mui/material";
import { clPageStyle, clContentBox, clCardSx, clCardListSx, clCentredState, CL_PRIMARY, ClPageHeader, ClPageTitle } from "./cl-styles";
import { paths } from "../App";
import { makeDate_Std } from "../utils/date-helper";
import { storageCleanReset } from "../utils/storage-clean-reset";
import { getSelectedOrgIds } from "../utils/orgSelection";

export const ChecklistMySavedDrafts = (props) => {

    const cache_key = 'cache_instance_';

    const [authUser, setAuthUser] = useState(getStorage('u4'));
    const [isLoading, setIsLoading] = useState(false);
    const [initialized, setInitialized] = useState(false);

    /* Navigation */
    let navigate = useNavigate();
    const goBack = () => navigate(paths.checklist.dash, { replace: true });

    const renderListHeader = () => <></>;
    const renderListFooter = () => <></>;

    const [myInstanceDrafts, setMyInstanceDrafts] = useState(null);
    const [draftsInOtherOrgs, setDraftsInOtherOrgs] = useState(0);

    // Initialize database and migrate data on component mount
    useEffect(() => {
        const initialize = async () => {
            setIsLoading(true);
            try {
                // Attempt to migrate data from localStorage to IndexedDB
                await migrateToIndexedDB();

                // Clean up any invalid entries in IndexedDB
                try {
                    const removedCount = await cleanupIndexedDB();
                    if (removedCount > 0) {
                        console.log(`Cleaned up ${removedCount} invalid checklist entries`);
                    }
                } catch (error) {
                    console.warn("Failed to cleanup IndexedDB:", error);
                }

                setInitialized(true);
            } catch (error) {
                console.warn("Failed to initialize IndexedDB:", error);
                // Still set initialized so we can proceed even with localStorage
                setInitialized(true);
            } finally {
                setIsLoading(false);
            }
        };

        initialize();
    }, []);

    // Fetch saved checklists when component is initialized
    useEffect(() => {
        if (initialized) {
            getMyClInstances();
        }
    }, [initialized]);

    // Helper function to extract a valid date from various data structures
    const extractTimestamp = (data) => {
        // Check all possible timestamp locations in order of preference
        const timestamp = data.timestamp ||
            data.data?.timestamp ||
            (data.data?.clInstance?.updatedAt ? new Date(data.data.clInstance.updatedAt) : null) ||
            (data.clInstance?.updatedAt ? new Date(data.clInstance.updatedAt) : null) ||
            new Date();

        // Return a valid Date object
        return timestamp instanceof Date ? timestamp : new Date(timestamp);
    };

    // Helper function to extract a name from various data structures
    const extractName = (data) => {
        return data.data?.clInstance?.name ||
            data?.clInstance?.name ||
            `Checklist ${data.id || data.data?.clInstance?.id || data?.clInstance?.id || 'Unknown'}`;
    };

    // Helper function to extract ID consistently
    const extractId = (data) => {
        let id = data.id || data.data?.clInstance?.id || data?.clInstance?.id || null;
        return id ? String(id) : null;
    };

    // Helper function to check if checklist has images
    const hasImages = (data) => {
        // Check for image references in indexed DB data
        if (data.data && data.data.imageReferences && Array.isArray(data.data.imageReferences)) {
            return data.data.imageReferences.length > 0;
        }
        // Check for images in the imageArray (legacy format or already loaded)
        if (data.data && data.data.imageArray && Array.isArray(data.data.imageArray)) {
            return data.data.imageArray.some(img => img && img.img && img.id);
        }
        // For legacy localStorage data
        if (data.imageArray && Array.isArray(data.imageArray)) {
            return data.imageArray.some(img => img && img.img && img.id);
        }
        return false;
    };

    // Helper function to get image count
    const getImageCount = (data) => {
        if (data.data && data.data.imageReferences && Array.isArray(data.data.imageReferences)) {
            return data.data.imageReferences.length;
        }
        if (data.data && data.data.imageArray && Array.isArray(data.data.imageArray)) {
            return data.data.imageArray.filter(img => img && img.img && img.id).length;
        }
        if (data.imageArray && Array.isArray(data.imageArray)) {
            return data.imageArray.filter(img => img && img.img && img.id).length;
        }
        return 0;
    };

    const getMyClInstances = async () => {
        if (!authUser || !authUser?.id) return;
        setIsLoading(true);

        try {
            // Use getAllSavedChecklists which prioritizes IndexedDB 
            const allChecklists = await getAllSavedChecklists();

            // Filter out any invalid entries
            const validChecklists = allChecklists.filter(item => {
                const id = extractId(item);
                return id !== null && id !== "undefined" && id !== "0";
            });

            // Sort by timestamp, newest first
            validChecklists.sort((a, b) => {
                const dateA = extractTimestamp(a);
                const dateB = extractTimestamp(b);
                return dateB - dateA; // Descending order (newest first)
            });

            const selectedOrgIds = getSelectedOrgIds(authUser);
            const visible = validChecklists.filter((item) => {
                const orgId = Number(item?.data?.clInstance?.org_id ?? item?.clInstance?.org_id);
                return !orgId || selectedOrgIds.includes(orgId);
            });

            setDraftsInOtherOrgs(validChecklists.length - visible.length);
            setMyInstanceDrafts(visible);
        } catch (error) {
            console.error("Failed to get saved checklists:", error);
            setMyInstanceDrafts([]);
        } finally {
            setIsLoading(false);
        }
    }

    // Delete confirmation
    const [delConfirmIsOpen, setDelConfirmIsOpen] = useState(false);

    const deleteCl = async () => {
        setDelConfirmIsOpen(false);
        setIsLoading(true);

        try {
            if (myInstanceDrafts && myInstanceDrafts.length > 0) {
                // Track deleted checklists to avoid duplicates
                const deletedIds = new Set();

                // Delete each checklist individually using IndexedDB-focused function
                for (const draft of myInstanceDrafts) {
                    // Get the instance ID consistently
                    const instanceId = extractId(draft);

                    if (!instanceId) {
                        console.warn('Skipping draft with no valid ID:', draft);
                        continue;
                    }

                    // Skip if we've already deleted this ID
                    if (deletedIds.has(instanceId)) {
                        console.log(`Skipping already deleted checklist ${instanceId}`);
                        continue;
                    }

                    await deleteChecklistData(instanceId);
                    deletedIds.add(instanceId);
                    console.log(`Deleted checklist ${instanceId}`);
                }
                console.log(`All checklists deleted successfully (${deletedIds.size} total)`);
            }
        } catch (error) {
            console.error("Failed to delete checklists:", error);
        } finally {
            setIsLoading(false);
            // Refresh the list to reflect changes
            getMyClInstances();
        }
    }

    /* Delete all locally stored data */
    const deleteAllLocalData = () => {
        // Close any open modals or popups
        setDelConfirmIsOpen(false);
        setIsLoading(true);

        try {
            /* Clean Reset */
            storageCleanReset();
        } catch (error) {
            console.error("Error during storage reset:", error);
        } finally {
            setIsLoading(false);
            // Refresh the list
            getMyClInstances();
        }
    };

    return (<>
        <Page renderToolbar={() => <ClPageHeader onBack={goBack} />} style={clPageStyle}>
            {/* Full-screen loading overlay */}
            {isLoading && (
                <Box sx={{ position: 'absolute', inset: 0, zIndex: 100, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.95)' }}>
                    <CircularProgress size={36} sx={{ color: CL_PRIMARY }} />
                    <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>Loading…</Typography>
                </Box>
            )}

            <Box sx={clContentBox}>
                <ClPageTitle title="My Saved Drafts" subtitle="These are checklists that you have saved locally and have not submitted yet. You need an active internet connection to submit them." />

                {/* Empty state */}
                {myInstanceDrafts !== null && myInstanceDrafts.length === 0 && (
                    <Box sx={clCentredState}>
                        <Typography color="text.secondary">
                            {draftsInOtherOrgs > 0 ? 'No drafts for the selected organisations' : 'No saved drafts found'}
                        </Typography>
                    </Box>
                )}

                {/* List */}
                {myInstanceDrafts && myInstanceDrafts.length > 0 && (
                    <Box sx={{ ...clCardListSx, mt: 0.5 }}>
                        {myInstanceDrafts.map((data, i) => {
                            const instanceId = extractId(data);
                            const name = extractName(data);
                            const timestamp = extractTimestamp(data);
                            const hasImagesFlag = hasImages(data);
                            const imageCount = getImageCount(data);
                            return (
                                <Paper key={i} elevation={0} sx={clCardSx}>
                                    <ListItemButton
                                        sx={{ px: 2, py: 1.25, alignItems: 'flex-start', gap: 1 }}
                                        onClick={() => navigate(paths.checklist.details, {
                                            state: { data: { localDecode: true, clInstance_id: instanceId } }
                                        })}
                                    >
                                        <Box sx={{ flex: 1, minWidth: 0 }}>

                                            {/* Name */}
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.5 }}>
                                                <Icon icon="mdi:file-document-outline" style={{ fontSize: 16, color: CL_PRIMARY, flexShrink: 0, marginTop: 2 }} />
                                                <Typography variant="body2" fontWeight={700} sx={{ color: '#1a1a1a', lineHeight: 1.3, fontSize: '0.9rem' }}>
                                                    {name}
                                                </Typography>
                                            </Box>

                                            {/* Last saved */}
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.4 }}>
                                                <Icon icon="mdi:clock-outline" style={{ fontSize: 15, color: '#888', flexShrink: 0 }} />
                                                <Typography sx={{ color: '#555', fontSize: '0.78rem' }}>
                                                    <span style={{ fontWeight: 700, color: '#888', textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: '0.04em' }}>Last saved </span>
                                                    {makeDate_Std(timestamp.toISOString())}
                                                </Typography>
                                            </Box>

                                            {/* Storage source */}
                                            {data.source && (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.4 }}>
                                                    <Icon icon="mdi:database-outline" style={{ fontSize: 15, color: '#888', flexShrink: 0 }} />
                                                    <Typography sx={{ color: '#444', fontSize: '0.78rem' }}>
                                                        <span style={{ fontWeight: 700, color: '#888', textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: '0.04em' }}>Stored </span>
                                                        {data.source}
                                                    </Typography>
                                                </Box>
                                            )}

                                            {/* ID + images chip */}
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Icon icon="mdi:pound" style={{ fontSize: 15, color: '#aaa', flexShrink: 0 }} />
                                                <Typography sx={{ color: '#777', fontFamily: 'monospace', fontSize: '0.78rem' }}>
                                                    {instanceId}
                                                </Typography>
                                                {hasImagesFlag && (
                                                    <Chip
                                                        icon={<Icon icon="mdi:camera-outline" style={{ fontSize: 11 }} />}
                                                        label={`${imageCount} ${imageCount === 1 ? 'image' : 'images'}`}
                                                        size="small"
                                                        sx={{ height: 20, fontSize: '0.65rem', backgroundColor: '#e8f4ff', color: '#0066cc', ml: 0.5 }}
                                                    />
                                                )}
                                            </Box>

                                        </Box>

                                        {/* Right: tap hint */}
                                        <Box sx={{ flexShrink: 0, alignSelf: 'center' }}>
                                            <Icon icon="mdi:chevron-right" style={{ fontSize: 20, color: '#ccc' }} />
                                        </Box>
                                    </ListItemButton>
                                </Paper>
                            );
                        })}
                    </Box>
                )}

                {draftsInOtherOrgs > 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                        {draftsInOtherOrgs} more unsubmitted checklist{draftsInOtherOrgs === 1 ? '' : 's'} in organisations you have not selected. Tick them on the dashboard to see them.
                    </Typography>
                )}

                {/* Delete button */}
                {(myInstanceDrafts === null || myInstanceDrafts?.length > 0) && (
                    <Box sx={{ mt: 3 }}>
                        <Button
                            onClick={() => setDelConfirmIsOpen(true)}
                            style={{ backgroundColor: "#753020", borderColor: "#753020" }}
                            disabled={isLoading}
                        >
                            Delete all
                        </Button>
                    </Box>
                )}

                {/* Delete confirmation dialog */}
                <AlertDialog isOpen={delConfirmIsOpen} onCancel={() => setDelConfirmIsOpen(false)} cancelable>
                    <div className="alert-dialog-title">Delete All Saved Checklists</div>
                    <div className="alert-dialog-content">
                        Are you sure you want to delete the {myInstanceDrafts?.length ?? 0} saved
                        {' '}draft{(myInstanceDrafts?.length ?? 0) === 1 ? '' : 's'} listed here? <br />
                        {draftsInOtherOrgs > 0 && (
                            <>Drafts in organisations you have not selected ({draftsInOtherOrgs}) are kept. <br /></>
                        )}
                        This action cannot be undone.

                        <div className="flex flex-dir-row flex-horiz-space-bw flex-vert-center p-t-20">
                            <Button modifier="quiet" onClick={() => setDelConfirmIsOpen(false)}>
                                Cancel
                            </Button>
                            <Button
                                onClick={deleteCl}
                                style={{ backgroundColor: "#753020", borderColor: "#753020" }}>
                                Delete
                            </Button>
                        </div>
                    </div>
                </AlertDialog>
            </Box>
        </Page>
    </>);
}
